/**
 * Optimizer (/optimizer) form logic, shared by both looks (v1.70):
 * screens/SameGameParlay.jsx (Floodlight) and screens/classic/SameGameParlay.jsx.
 * State, data loading (seed fixtures, league games, player roster with the
 * latest-request-wins guard), and the handlers that turn the form into search
 * text. The filter rules themselves live in utils/optimizerFilters.js.
 * Tested by hooks/__tests__/useOptimizerForm.test.js and both screen tests.
 */
import { useState, useRef, useEffect } from 'react';
import { track } from '../utils/analytics';
import MARKET_MAPPING from '../data/marketMapping.json';
import { useNavigation } from '../context/NavigationContext';
import { useBackgroundQuery } from '../context/BackgroundQueryContext';
import { useAuth } from '../context/AuthContext';
import { sportsData, getMarkets, getApiLeague, getSportCode, MARKET_CATEGORIES, ALL_SPORTSBOOKS as ALL_SPORTSBOOKS_DATA } from '../data/sportsData';
import {
    leagueOptions, teamOptions, marketOptions, fixtureOptions, sportChange, leagueChange, findTeamHome,
    lineProblem, describeSelection, sgpQuery, legProblem, teamSearchKeyword,
} from '../utils/optimizerFilters';
import { PROD_URL, getFunctionsBaseUrl } from '../data/optimizerBooks';
import { db } from '../firebaseConfig';
import { doc, onSnapshot } from 'firebase/firestore';
import { dataFetch } from '../utils/dataSource';

// Server order kept for a name search (best match first); rosters are sorted.
const rankedNames = (data) => (Array.isArray(data?.players) ? [...new Set(data.players.map((p) => p.name).filter(Boolean))] : null);
const playerNames = (data) => rankedNames(data)?.sort() ?? null;
const fetchJson = (url) => fetch(url).then((r) => r.json());

export default function useOptimizerForm() {
    const { navigateTo, hasComingSoonAccess } = useNavigation();
    const { userProfile } = useAuth();
    const { addBackgroundQuery, canAddBackgroundQuery } = useBackgroundQuery();
    const backgroundQueryMode = userProfile?.preferences?.backgroundQueryMode === true;

    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [notice, setNotice] = useState('');
    const [betType, setBetType] = useState('Straight');

    // Filters
    const [selectedSport, setSelectedSport] = useState('');
    const [selectedLeague, setSelectedLeague] = useState('');
    const [selectedTeam, setSelectedTeam] = useState('');
    const [selectedPlayer, setSelectedPlayer] = useState('');
    const [selectedMarket, setSelectedMarket] = useState('');
    const [selectedMarketCategory, setSelectedMarketCategory] = useState('');
    const [selectedSportsbooks, setSelectedSportsbooks] = useState([]);
    const [selectedTimeFilter, setSelectedTimeFilter] = useState(null);
    const [selectedFixture, setSelectedFixture] = useState(null);
    const [lineValue, setLineValue] = useState('');
    const [lineDirection, setLineDirection] = useState('');
    const [boostPercent] = useState('0'); // Profit boost: beta, not wired yet
    const [showSportsbooksDropdown, setShowSportsbooksDropdown] = useState(false);

    // Cached data: the mount fetch seeds leagues/teams/markets; games load per league.
    const [seedFixtures, setSeedFixtures] = useState([]);
    const [leagueFixtures, setLeagueFixtures] = useState([]);
    const [loadingCacheData, setLoadingCacheData] = useState(false);
    const [cacheError, setCacheError] = useState(false);
    const [loadingFixtures, setLoadingFixtures] = useState(false);
    const [playerOptions, setPlayerOptions] = useState([]);
    const [loadingPlayers, setLoadingPlayers] = useState(false);

    const [queries, setQueries] = useState([]); // Straight: added queries
    const [legs, setLegs] = useState([]);       // SGP: legs, all from one game
    const nextId = useRef(1);

    const [showComingSoon, setShowComingSoon] = useState(false);

    const filterState = {
        sport: selectedSport, league: selectedLeague, team: selectedTeam, fixture: selectedFixture,
        player: selectedPlayer, market: selectedMarket, category: selectedMarketCategory, line: lineValue, direction: lineDirection,
    };
    const SETTERS = {
        sport: setSelectedSport, league: setSelectedLeague, team: setSelectedTeam, fixture: setSelectedFixture,
        player: setSelectedPlayer, market: setSelectedMarket, category: setSelectedMarketCategory, line: setLineValue, direction: setLineDirection,
    };
    const applyPatch = (patch) => Object.entries(patch).forEach(([k, v]) => SETTERS[k]?.(v));

    // SGP "Coming soon" gate from Firestore
    useEffect(() => {
        const unsub = onSnapshot(doc(db, 'site_maintenance', 'global'), (snap) => {
            const enabled = !!(snap.data() || {}).sgpComingSoonEnabled;
            setShowComingSoon(enabled && !hasComingSoonAccess);
        });
        return () => unsub();
    }, [hasComingSoonAccess]);

    // Seed leagues/teams/markets once from the cached fixture list.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoadingCacheData(true);
            try {
                const response = await dataFetch(getFunctionsBaseUrl(), 'getAllCachedFixtures?limit=50');
                if (!response.ok) {
                    console.warn('[Optimizer] getAllCachedFixtures returned', response.status, '— using static data');
                    if (!cancelled) setCacheError(true);
                    return;
                }
                const data = await response.json();
                if (!cancelled && Array.isArray(data.fixtures)) {
                    setSeedFixtures(data.fixtures.map((f) => ({ league: f.league, home_team: f.home_team, away_team: f.away_team, markets: f.markets })));
                }
            } catch (error) {
                console.warn('[Optimizer] Error fetching cache data (will use static data):', error.message);
                if (!cancelled) setCacheError(true);
            } finally {
                if (!cancelled) setLoadingCacheData(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Games for the picked league (team picks don't refetch).
    useEffect(() => {
        if (!selectedLeague || !selectedSport) {
            setLeagueFixtures([]);
            setLoadingFixtures(false);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            setLoadingFixtures(true);
            try {
                const apiLeague = getApiLeague(selectedSport, selectedLeague);
                const response = await dataFetch(getFunctionsBaseUrl(), `getAllCachedFixtures?league=${apiLeague}&include_live=true&limit=50`);
                if (!response.ok) {
                    console.warn('[Optimizer] Fixture fetch returned', response.status);
                    return;
                }
                const data = await response.json();
                if (!cancelled && Array.isArray(data.fixtures)) {
                    setLeagueFixtures(data.fixtures.map((f) => ({
                        id: f.id, home_team: f.home_team, away_team: f.away_team, start_date: f.start_date, league: f.league, total_markets: f.total_markets,
                    })));
                }
            } catch (e) {
                console.warn('[Optimizer] Could not load fixtures:', e.message);
            } finally {
                if (!cancelled) setLoadingFixtures(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedLeague, selectedSport]);

    // Player roster: the game's two teams, else the team, else the league (or the sport's first league), else everyone.
    const rosterFixture = betType === 'SGP' ? selectedFixture : null;
    const fixtureKey = rosterFixture ? rosterFixture.id : '';
    // The roster and the name search both fill playerOptions: only the latest request may
    // write. Unless a game or team narrows the roster, a typed name hands over to the
    // search (the league list stops at 500 names, so it may not hold the player), and
    // clearing it brings the roster back.
    const playerReqRef = useRef(0);
    const nameSearchActive = !rosterFixture && !selectedTeam && (selectedPlayer || '').length >= 2;
    useEffect(() => {
        if (nameSearchActive) return undefined;
        const req = ++playerReqRef.current;
        let cancelled = false;
        const load = async () => {
            setLoadingPlayers(true);
            try {
                let urls;
                if (!selectedSport) {
                    urls = [`${PROD_URL}/getPlayersCache?limit=3334`];
                } else {
                    const league = selectedLeague || sportsData[selectedSport]?.leagues?.[0];
                    if (!league) return;
                    const apiLeague = getApiLeague(selectedSport, league);
                    const teams = rosterFixture ? [rosterFixture.away_team, rosterFixture.home_team] : selectedTeam ? [selectedTeam] : [];
                    urls = teams.length
                        ? teams.map((t) => `${PROD_URL}/getPlayersCache?league=${apiLeague}&search=${encodeURIComponent(teamSearchKeyword(t))}&limit=150`)
                        : [`${PROD_URL}/getPlayersCache?league=${apiLeague}&limit=500`];
                }
                const results = await Promise.all(urls.map(fetchJson));
                const names = results.map(playerNames).filter(Boolean);
                if (!cancelled && req === playerReqRef.current && names.length) setPlayerOptions([...new Set(names.flat())].sort());
            } catch (e) {
                console.warn('[Optimizer] Could not load players:', e.message);
            } finally {
                if (!cancelled && req === playerReqRef.current) setLoadingPlayers(false);
            }
        };
        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSport, selectedLeague, selectedTeam, fixtureKey, nameSearchActive]);

    // Typing a name searches every player, or the league's (debounced). Typos are
    // matched server-side ("Rogers" → Aaron Rodgers).
    // A name picked from the list (or typed out in full) keeps the list it came from.
    const playerOptionsRef = useRef(playerOptions);
    playerOptionsRef.current = playerOptions;
    useEffect(() => {
        if (!nameSearchActive) return undefined;
        if (playerOptionsRef.current.includes(selectedPlayer)) return undefined;
        let cancelled = false;
        const timer = setTimeout(async () => {
            const req = ++playerReqRef.current;
            setLoadingPlayers(true);
            try {
                const league = selectedSport ? (selectedLeague || sportsData[selectedSport]?.leagues?.[0]) : '';
                const scope = league ? `league=${getApiLeague(selectedSport, league)}&` : '';
                const names = rankedNames(await fetchJson(`${PROD_URL}/getPlayersCache?${scope}search=${encodeURIComponent(selectedPlayer)}&limit=30`));
                if (!cancelled && req === playerReqRef.current && names) setPlayerOptions(names);
            } catch (e) {
                // the player field still works as free text
            } finally {
                if (!cancelled && req === playerReqRef.current) setLoadingPlayers(false);
            }
        }, 400);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [selectedPlayer, selectedLeague, selectedSport, nameSearchActive]);

    // Options
    const cachedLeagues = [...new Set(seedFixtures.map((f) => f.league).filter(Boolean))];
    const leagues = leagueOptions(selectedSport, cachedLeagues);
    const games = fixtureOptions(leagueFixtures, selectedSport, selectedLeague);
    const teams = teamOptions({ sport: selectedSport, league: selectedLeague, fixture: betType === 'SGP' ? selectedFixture : null, seedFixtures, leagueFixtures });
    const markets = marketOptions({
        sport: selectedSport, category: selectedMarketCategory, sportMarkets: MARKET_MAPPING.sportToMarkets || {}, seedFixtures,
        fallback: (s) => getMarkets(s, 'all'),
    });
    const categories = Object.keys(MARKET_CATEGORIES).filter((c) => !selectedPlayer || c === 'All Markets' || c === 'Player Props');
    const lineIssue = lineProblem(lineValue, lineDirection);

    // Handlers
    const handleSportSelect = (sport) => applyPatch(sportChange(filterState, sport));
    const handleLeagueSelect = (league) => applyPatch(leagueChange(filterState, league));

    const handleTeamSelect = (team) => {
        setSelectedTeam(team);
        if (!team) return;
        const home = findTeamHome(team);
        if (home) {
            if (!selectedSport) setSelectedSport(home.sport);
            if (!selectedLeague) setSelectedLeague(home.league);
        }
        if (selectedFixture && selectedFixture.home_team !== team && selectedFixture.away_team !== team) setSelectedFixture(null);
    };

    const handleMarketSelect = (market) => {
        setSelectedMarket(market);
        if (market && !selectedSport) {
            const mSports = MARKET_MAPPING.marketToSports?.[market] || [];
            if (mSports.length === 1) setSelectedSport(mSports[0]);
        }
    };

    const handlePlayerSelect = (v) => {
        setSelectedPlayer(v);
        if (v && ['Game Lines', 'Quarter / Half', 'Team Props'].includes(selectedMarketCategory)) setSelectedMarketCategory('Player Props');
    };

    const handleFixtureSelect = (label) => {
        const fixture = games.find((f) => f.label === label) || null;
        setSelectedFixture(fixture);
        if (selectedTeam && fixture && fixture.home_team !== selectedTeam && fixture.away_team !== selectedTeam) setSelectedTeam('');
        // A parlay is one game: legs from another game don't carry over.
        if (legs.length && (!fixture || legs[0].fixture.id !== fixture.id)) {
            setLegs([]);
            setNotice(fixture ? 'New game, new parlay: the legs from the last game were cleared.' : '');
        } else {
            setNotice('');
        }
    };

    const selection = {
        sport: selectedSport, league: selectedLeague, team: selectedTeam, player: selectedPlayer, market: selectedMarket,
        line: lineValue, direction: lineDirection, timeFilter: selectedTimeFilter, sportsbooks: selectedSportsbooks,
    };

    const clearBetFields = () => {
        setSelectedTeam('');
        setSelectedPlayer('');
        setSelectedMarket('');
        setLineValue('');
        setLineDirection('');
        setErrorMsg('');
    };

    const addQuery = () => {
        if (lineIssue) { setErrorMsg(lineIssue); return; }
        const text = describeSelection(selection);
        if (!text) { setErrorMsg('Pick at least one filter before adding a query.'); return; }
        setQueries((prev) => [...prev, { id: nextId.current++, text }]);
        clearBetFields();
    };

    const addLeg = () => {
        const problem = legProblem({ fixture: selectedFixture, team: selectedTeam, player: selectedPlayer, market: selectedMarket, legs }) || lineIssue;
        if (problem) { setErrorMsg(problem); return; }
        const description = describeSelection(selection, { forLeg: true });
        setLegs((prev) => [...prev, { id: nextId.current++, fixture: selectedFixture, league: selectedLeague, description }]);
        setNotice('');
        clearBetFields();
    };

    const clearAllFilters = () => {
        applyPatch(sportChange(filterState, ''));
        setSelectedSportsbooks([]);
        setSelectedTimeFilter(null);
        setLegs([]);
        setQueries([]);
        setErrorMsg('');
        setNotice('');
    };

    const getFinalQuery = () => {
        if (betType === 'SGP') return sgpQuery(legs[0]?.fixture, legs[0]?.league || '', legs);
        const current = lineIssue ? '' : describeSelection(selection);
        return [...queries.map((q) => q.text), current].filter(Boolean).join(' AND ');
    };

    const runSearch = async (forceBackground = false) => {
        if (betType === 'Straight' && lineIssue) { setErrorMsg(lineIssue); return; }
        const query = getFinalQuery();
        if (!query) {
            setErrorMsg(betType === 'SGP' ? 'Add at least one leg to your same-game parlay.' : 'Pick at least one filter or add a query.');
            return;
        }
        // A press event must never count as "force background".
        const forced = forceBackground === true;
        setErrorMsg('');
        const useBackground = forced || (backgroundQueryMode && canAddBackgroundQuery);
        track(betType === 'SGP' ? 'sgp_build' : 'search', { from: 'optimizer', legs: legs.length, background: useBackground });

        const fixture = betType === 'SGP' ? legs[0]?.fixture : null;
        const filters = {
            sport: getSportCode(selectedSport),
            league: getApiLeague(selectedSport, selectedLeague),
            team: selectedTeam,
            player: selectedPlayer,
            market: selectedMarket,
            sportsbooks: selectedSportsbooks,
            ...(fixture ? { fixtureId: fixture.id, fixture: `${fixture.away_team} @ ${fixture.home_team}` } : {}),
        };

        if (useBackground) {
            if (!canAddBackgroundQuery) {
                setErrorMsg('Your background searches are full. Wait for one to finish or run this one now.');
                return;
            }
            setLoading(true);
            try {
                await addBackgroundQuery(query, { ...filters, fromOptimizer: true });
            } catch (e) {
                setErrorMsg('Could not queue that search. Try again, or run it now.');
            } finally {
                setLoading(false);
            }
            return;
        }
        navigateTo('SgpFlow', { payload: { query, fromOptimizer: true, filters: { ...filters, timeFilter: selectedTimeFilter } } });
    };

    const toggleSportsbook = (book) => setSelectedSportsbooks((prev) => (prev.includes(book) ? prev.filter((b) => b !== book) : [...prev, book]));

    const switchMode = (mode) => {
        // Each mode keeps its own legs/queries, so switching back loses nothing.
        setBetType(mode);
        setErrorMsg('');
        setNotice('');
    };

    const finalQuery = getFinalQuery();
    const sgp = betType === 'SGP';

    const myBookNames = (userProfile?.preferences?.myBooks || []).map((id) => {
        const byId = ALL_SPORTSBOOKS_DATA.find((b) => b.id === id);
        if (byId) return byId.name;
        const byName = ALL_SPORTSBOOKS_DATA.find((b) => b.name?.toLowerCase() === (id || '').toLowerCase());
        return byName ? byName.name : null;
    }).filter(Boolean);

    const removeLeg = (id) => setLegs((p) => p.filter((l) => l.id !== id));
    const removeQuery = (id) => setQueries((p) => p.filter((q) => q.id !== id));

    return {
        navigateTo, canAddBackgroundQuery, backgroundQueryMode,
        loading, errorMsg, notice, betType, sgp, switchMode, showComingSoon,
        selectedSport, selectedLeague, selectedTeam, selectedPlayer, selectedMarket, selectedMarketCategory,
        selectedSportsbooks, setSelectedSportsbooks, selectedTimeFilter, setSelectedTimeFilter, selectedFixture,
        lineValue, setLineValue, lineDirection, setLineDirection, boostPercent,
        showSportsbooksDropdown, setShowSportsbooksDropdown,
        loadingCacheData, cacheError, leagueFixtures, loadingFixtures, playerOptions, loadingPlayers,
        leagues, games, teams, markets, categories, lineIssue,
        handleSportSelect, handleLeagueSelect, handleTeamSelect, handleMarketSelect, handlePlayerSelect, handleFixtureSelect,
        handleCategorySelect: (v) => { setSelectedMarketCategory(v); setSelectedMarket(''); },
        queries, legs, addQuery, addLeg, removeQuery, removeLeg, clearAllFilters,
        finalQuery, runSearch, toggleSportsbook, myBookNames,
    };
}
